import React, { FunctionComponent, useState, useEffect } from 'react';
import * as vp from 'vopay';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { Box, Button, Table, TextField, Select, Icon } from 'vopay';
import type { CreateLeadHandlers } from '../types';

import {
  useBuildLeadCreationRows,
  buildInsideSalesPartnerLeadCreationRows,
  buildInsideSalesAgentLeadCreationRows,
  buildISOPartnerDataAggregatorLeadCreationRows,
  buildISOSystemOfRecordLeadCreationRow,
} from './buildLeadCreationRows.helper';

import useFetchPartners from '@/hooks/useApi/useFetchPartners';
import useFetchUserLists from '@/hooks/useApi/useFetchUserLists';
import { usePartnerLogin } from '@/providers/PartnerLogin.provider';
import { useTranslation } from 'react-i18next';

type LeadCreationProps = {
  handlers: CreateLeadHandlers;
  defaultValues: vp.CreateLeadRequest;
  reviewing: boolean;
};

type FC = FunctionComponent<LeadCreationProps>;
const LeadCreationForm: FC = (props: LeadCreationProps) => {
  const { handlers, defaultValues, reviewing } = props;
  const { response: loginResponse } = usePartnerLogin();
  const { t } = useTranslation('translations', { keyPrefix: 'Leads.Create' });

  const leadsRow = useBuildLeadCreationRows();
  const insideSalesPartnerLeadRow = buildInsideSalesPartnerLeadCreationRows();
  const insideSalesAgentLeadRow = buildInsideSalesAgentLeadCreationRows();
  const isoPartnerDataAggregatorLeadCreationRow = buildISOPartnerDataAggregatorLeadCreationRows();
  const isoSystemOfRecordLeadCreationRow = buildISOSystemOfRecordLeadCreationRow();
  const partnerType = loginResponse?.partnerType || '';
  const isInsideSales = loginResponse.isInsideSales || false;
  const isRoleViewOnly = loginResponse.isRoleViewOnly;

  const [partners, fetchPartners] = useFetchPartners({});
  const [userLists, fetchUserLists] = useFetchUserLists({});
  const [partnerName, setPartnerName] = useState<string>('');
  const [agentOptions, setAgentOptions] = useState<{ [key: string]: string }>({});
  const [buttonIsDisabled, setButtonIsDisabled] = useState<boolean>(true);

  const partnerOptions: { [key: string]: string } = Object.values(partners.PartnerAccount || {}).reduce(
    (partnersList, partner) => ({ ...partnersList, [partner.Username]: partner.Name }),
    {},
  );

  useEffect(() => {
    fetchPartners();
  }, []);

  useEffect(() => {
    fetchUserLists(partnerName);
  }, [partnerName]);

  useEffect(() => {
    setAgentOptions(
      Object.values(userLists.PartnerUsers || {}).reduce(
        (agents, agent) => ({ ...agents, [agent.Username]: agent.Username }),
        {},
      ),
    );
  }, [userLists]);

  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={reviewing ? handlers.onCreateLead : handlers.onReview}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
    >
      {({ values, touched, errors, handleChange, handleBlur, handleSubmit }) => {
        const handlePartnerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          setPartnerName(event.currentTarget.value);
          handleChange(event);
          setAgentOptions({});
        };

        useEffect(() => {
          const hasErrors = Object.entries(errors).length > 0;
          const filteredArr = Object.values(values).filter((value) => typeof value !== 'boolean');
          const allEmptyFields = Object.values(filteredArr).every((val) => !val);

          setButtonIsDisabled(allEmptyFields || hasErrors);
        }, [errors]);

        const leadsTable: vp.TableProps = {
          name: 'add-user-fields',
          variant: 'pair',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: leadsRow.map(({ label, id, preset, require }) => ({
            Field: (
              <Box ai="center">
                {label} {getRequiredIcon(require)}
              </Box>
            ),

            Input: (
              <TextField
                preset={preset}
                key={id}
                name={id}
                variant="standard"
                onBlur={handleBlur}
                onChange={handleChange}
                error={errors[id] && touched[id] ? errors[id] : null}
                disabled={reviewing}
              />
            ),
          })),
        };
        const ISODataAggregatorTable: vp.TableProps = {
          name: 'add-data-aggregator-partner-fields',
          variant: 'pair',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: isoPartnerDataAggregatorLeadCreationRow.map(({ label, require }) => ({
            Field: (
              <Box ai="center">
                {label} {getRequiredIcon(require)}
              </Box>
            ),
            Input: (
              <Select
                variant="plain"
                options={vp.DATA_AGGREGATOR_OPTIONS}
                placeholder="Data Aggregator"
                name="DataAggregator"
                value=""
                onBlur={handleBlur}
                onChange={handleChange}
                disabled={reviewing}
              />
            ),
          })),
        };
        const ISOSystemOfRecordTable: vp.TableProps = {
          name: 'add-data-aggregator-partner-fields',
          variant: 'pair',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: isoSystemOfRecordLeadCreationRow.map(({ label, require }) => ({
            Field: (
              <Box ai="center">
                {label} {getRequiredIcon(require)}
              </Box>
            ),
            Input: (
              <TextField
                variant="plain"
                placeholder={t('isoSystemInputPlaceholder')}
                name="SystemOfRecord"
                key="SystemOfRecord"
                onBlur={handleBlur}
                onChange={handleChange}
                disabled={reviewing}
              />
            ),
          })),
        };
        const insideSalesPartnerLeadsTable: vp.TableProps = {
          name: 'add-inside-sales-partner-fields',
          variant: 'pair',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: insideSalesPartnerLeadRow.map(({ label, require }) => ({
            Field: (
              <Box ai="center">
                {label} {getRequiredIcon(require)}
              </Box>
            ),
            Input: (
              <Select
                variant="plain"
                options={partnerOptions}
                placeholder={t('insideSalesPartnerInputPlaceholder')}
                name="PartnerID"
                value={partnerName}
                onBlur={handleBlur}
                onChange={handlePartnerChange}
                disabled={reviewing}
              />
            ),
          })),
        };
        const insideSalesAgentLeadsTable: vp.TableProps = {
          name: 'add-inside-sales-agent-fields',
          variant: 'pair',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: insideSalesAgentLeadRow.map(({ label, require }) => ({
            Field: (
              <Box ai="center">
                {label} {getRequiredIcon(require)}
              </Box>
            ),
            Input: (
              <Select
                variant="plain"
                options={agentOptions}
                placeholder={t('insideSalesAgentInputPlaceholder')}
                name="AgentID"
                value={values.AgentID}
                onBlur={handleBlur}
                onChange={handleChange}
                disabled={reviewing}
              />
            ),
          })),
        };
        return (
          <form onSubmit={handleSubmit}>
            <Table {...leadsTable} />
            {partnerType == 'ISO' ? (
              <>
                <Table {...ISODataAggregatorTable} />
                <Table {...ISOSystemOfRecordTable} />
              </>
            ) : (
              ''
            )}
            {isInsideSales ? (
              <>
                <Table {...insideSalesPartnerLeadsTable} />
                <Table {...insideSalesAgentLeadsTable} />
              </>
            ) : (
              ''
            )}
            <Box mt="md" jc="fe">
              <Button
                id="CreateLead-submit"
                type="submit"
                label={reviewing ? t('Review.submitButton') : t('submitButton')}
                isDisabled={buttonIsDisabled || isRoleViewOnly}
              />
            </Box>
          </form>
        );
      }}
    </Formik>
  );
  function createdValidationSchema() {
    return Yup.object().shape({
      FirstName: Yup.string().label(t('firstName')).required(),
      LastName: Yup.string().label(t('lastName')).required(),
      BusinessEmail: Yup.string()
        .matches(
          /^(.*)@(?!(gmail|mail|email|usa|myself|consultant|europe|post|asia|iname|writeme|dr|cheerful|accountant|techie|engineer|linuxmail|musician|workmail|financier|fastservice|contractor|planetmail|homemail|hackermail|dbzmail|coolsite|australiamail|pacificwest|nycmail|kissfans|technologist|webname|null|greenmail|instructor|secratery|salesperson|repairman|deliveryman|allergist|execs|auctioneer|bartender|comic|yahoo|hotmail|msn|protonmail|zohomail|outlook|icloud|aol|tutanota|startmail|hushmail|mailfence|aweber|countermail|posteo|mail2one|mail2world|mail2usa|mail2seattle|mail2washington|mail2cool|mail2art|mail2fun|mail2dude|mail2expert|mail2consultant|thunderbird|yandex|ranren|mailasia|gmx|xwebtools|netscape|fexpost)).(.*)/,
          t('businessEmailValidation'),
        )
        .label(t('email'))
        .required()
        .email(t('emailValidation')),
      CompanyName: Yup.string().label(t('company')).required(),
      DBA: Yup.string().label(t('dba')).required(),
      AgentID: isInsideSales ? Yup.string().label(t('agentIDLabel')).required() : Yup.string().notRequired(),
      PartnerID: isInsideSales ? Yup.string().label(t('partnerIDLabel')).required() : Yup.string().notRequired(),
      DataAggregator:
        partnerType === 'ISO' ? Yup.string().label(t('dataAggregatorLabel')).required() : Yup.string().notRequired(),
      SystemOfRecord:
        partnerType === 'ISO' ? Yup.string().label(t('systemOfRecordLabel')).required() : Yup.string().notRequired(),
    });
  }
  function getRequiredIcon(isRequired: boolean): React.JSX.Element {
    return isRequired ? <Icon icon="asterisk" size="xsm" color="error" /> : <></>;
  }
};

export default LeadCreationForm;
